<template>
	<v-row>
		<v-col>

			<v-form v-if="this.partner" v-model="formIsValid.place" ref="placeForm">
				<ui-box :form="$refs.placeForm" new-forms>

					<form-title title="Place"></form-title>

					<form-divider inner/>

					<v-row>
						<v-col>
							<venue-select
								v-model="tasting.venue_id"
								:rules="rules.place"
								:venues="venues"
							></venue-select>
						</v-col>
					</v-row>
				</ui-box>
			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.voucherType" ref="voucherTypeForm">

				<ui-box :form="$refs.voucherTypeForm" :eager="!!product.id" new-forms>

					<form-title title="What type of item do you want to provide?">
						Choose the type of item you want to provide for this tasting.
					</form-title>

					<form-divider inner/>

					<!-- Child Template -->
					<v-row>
						<v-col>
							<v-select
									v-model="selectedTastingTemplate"
									class="rounded-lg required"
									hide-details="auto"
									:items="template.childTemplates"
									item-text="name"
									label="Voucher Type"
									outlined
									return-object
									:rules="rules.template_id"
							></v-select>
						</v-col>
					</v-row>
					<!-- End: Child Template -->

				</ui-box>

			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.details" ref="detailsForm">

				<ui-box :form="$refs.detailsForm" :eager="!!product.id" new-forms>

					<form-title title="Voucher Details"></form-title>

					<form-divider inner/>

					<span class="text-caption">Voucher Name</span>
					<h4 class="font-weight-light mb-6 mt-4 secondary--text">{{ tastingName }}</h4>
					<v-alert
							v-if="!validProductName"
							class="my-8 rounded-lg"
							border="left"
							color="warning"
							dense
							icon="mdi-alert-circle"
							text
					>
							<span :class="{ 'text-body-2': $vuetify.breakpoint.smAndDown }">
								<span class="font-weight-bold">Warning: </span>
								The current voucher name <strong>{{ tasting.name }}</strong> is not valid.
								You may update it below to fit your needs.
							</span>
					</v-alert>

					<!-- Voucher tasting -->
					<v-row>
						<v-col>
							<v-text-field
									v-model.number="tasting.settings.tasting_voucher_amount"
									v-if="isVoucherTasting"
									v-two-decimals
									class="rounded-lg required"
									label="What amount?"
									hide-spin-buttons
									hint="Minimum of $5"
									min="5"
									outlined
									persistent-hint
									:prefix="pricePrefix"
									:rules="isVoucherTasting && rules.tasting_voucher_amount || []"
									type="number"
									value="5"
									@wheel.native.prevent
							></v-text-field>
						</v-col>
					</v-row>
					<!-- End: Voucher tasting -->

					<!-- Free tasting -->
					<v-row v-if="isFreeTasting">
						<v-col>
							<v-text-field
									v-model="tasting.settings.tasting_free_item"
									class="rounded-lg required"
									label="Item Name"
									outlined
									persistent-hint
									hint="Example: Beer, Wine, Cider, Food Item, etc."
									:rules="isFreeTasting && rules.tasting_free_item"
							></v-text-field>
						</v-col>
					</v-row>

					<!-- Special Item  -->
					<v-row v-if="isSpecialItem">
						<v-col>
							<v-text-field
									v-model="tasting.settings.special_item_name"
									class="rounded-lg required"
									label="Item Name"
									outlined
									persistent-hint
									hint="Example: Beer, Wine, Cider, Food Item, etc."
									:rules="isSpecialItem && rules.special_item_name"
							></v-text-field>
						</v-col>
					</v-row>
					<!-- End: Special Item -->

					<!-- Tasting flight -->
					<v-row v-if="isTastingFlight">
						<v-col>
							<v-text-field
									v-model="tasting.settings.tasting_flight_type"
									class="rounded-lg required"
									label="Type of Tasting Flight"
									outlined
									persistent-hint
									hint="Example: Beer, Wine, Cider, Food Item, etc."
									:rules="isTastingFlight && rules.tasting_flight_type || []"
							></v-text-field>
						</v-col>
					</v-row>
					<!-- End: Tasting flight -->

					<!-- Included -->
					<v-row>
						<v-col>
							<wysiwyg-input
									v-model="tasting.short_information"
									:counter="1250"
									hide-details="auto"
									hint="Example: Ounces poured, type of food/beverage etc."
									label="What's Included"
									outlined
									persistent-hint
									rows="4"
									:rules="rules.brief_description"
							></wysiwyg-input>
						</v-col>
					</v-row>
					<!-- End: Included -->

					<!-- Restrictions -->
					<v-row>
						<v-col>
							<wysiwyg-input
									v-model="tasting.restrictions"
									:counter="1250"
									hide-details="auto"
									hint="If there are no restrictions on use for this offer, leave blank."
									label="Voucher Restrictions"
									outlined
									persistent-hint
									rows="4"
									:rules="rules.brief_description"
							></wysiwyg-input>
						</v-col>
					</v-row>
					<!-- End: Restrictions -->

					<v-form v-model="formIsValid.blackoutDates" ref="blackoutDatesForm" class="mb-8">
						<blackout-dates
								:refs="$refs"
								:product="product"
								:coupon="coupon"
								:form-is-valid="formIsValid"
								:timezone="partner?.timezone || timezone"
						/>
					</v-form>

					<!-- Prices -->
					<v-row>
						<v-col>
							<v-text-field
									v-model.number="tasting.retail_price"
									v-two-decimals
									:class="'rounded-lg' + (origin === 'partner-portal' ? '' : ' required')"
									hide-spin-buttons
									label="Retail Price"
									outlined
									persistent-hint
									:prefix="pricePrefix"
									:rules="origin !== 'partner-portal' && rules.retail_price || []"
									type="number"
									@wheel.native.prevent
							></v-text-field>
						</v-col>
						<v-col v-if="origin === 'partner-portal'">
							<v-text-field
									v-model.number="tasting.net_rate"
									v-two-decimals
									class="rounded-lg"
									hide-spin-buttons
									label="Remittance Rate"
									outlined
									persistent-hint
									:prefix="pricePrefix"
									type="number"
									@wheel.native.prevent
							></v-text-field>
						</v-col>
					</v-row>
					<!-- Prices -->
 

				</ui-box>

			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.redeemForm" ref="redeemForm" class="mb-8">
				<redeem-form :refs="$refs" :coupon="tasting" :form-is-valid="formIsValid"
							 :origin="origin" :product="product" :rules="rules"/>
			</v-form>

			<form-divider v-if="origin=='merchant-portal'"/>

			<v-form v-if="origin=='merchant-portal'" v-model="formIsValid.availability"
							ref="availabilityForm" class="mb-8">

				<ui-box :form="$refs.availabilityForm" :eager="!!product.id" new-forms>

					<form-title title="Product Availability"></form-title>

					<form-divider inner/>

					<v-row>
						<v-col cols="12">

							<!-- Added automatically -->
							<v-switch
									v-model="customFields.bw_add_to_future_passes.value"
									class="mb-4"
									dense
									false-value="No"
									hide-details="auto"
									inset
									label="Yes, include this product in future passes automatically."
									true-value="Yes"
							></v-switch>
							<!-- End: Added automatically -->

						</v-col>
					</v-row>
				</ui-box>
			</v-form>

			<mobile-pass-preview-modal
					v-if="preview.url"
					:preview-url="preview.url"
					@accept-preview="acceptPreview"
					@reject-preview="rejectPreview"
			></mobile-pass-preview-modal>

			<save-form
					:refs="$refs"
					:is-saving="isSaving"
					new-forms
					save-text="Save & Preview"
					@cancel-action="cancelChanges"
					@save-action="saveChanges"
			></save-form>
		</v-col>
	</v-row>
</template>

<script>
import {productHelpers, tastingHelpers} from '../helpers';
import {templateHelpers} from '../../../../templateHelpers';
import FormDivider from "../../../../UI/Form/FormDivider.vue";
import VenueSelect from '../Shared/VenueSelect.vue';
import BlackoutDates from "@app/Shared/Components/BlackoutDates.vue";
import ButtonLanguage from "../Shared/ButtonLanguage.vue";


export default {
	name: 'TastingProductForm',
	components: {ButtonLanguage, BlackoutDates, FormDivider, VenueSelect },
	props: {
		// The incoming product from an edit
		product: {
			type: Object,
			default: () => ({})
		},
		template: {
			type: Object,
			default: () => ({})
		},
		venue: {
			type: Object,
			default: () => ({})
		},
		venues: {
			type: Array,
			default: () => []
		},
		partner: {
			type: Object | null,
			default: null
		},
		returnUrl: {
			type: String,
			default: ''
		},
		origin: {
			type: String,
			default: ''
		},
		timezone: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			formIsValid: {
				voucherType: false,
				details: false,
				availability: false,
				redeemForm: false
			},
			tasting: {
				...productHelpers.productFormat(this.product)
			},
			customFields: {
				...productHelpers.customFields(this.origin, 'No')
			},
			isSaving: false,
			preview: {
				product: {},
				url: undefined
			},

			rules: {},
			selectedTastingTemplate: productHelpers.getTemplate(this.product, this.template, this.template.childTemplates.map(t => t.id)),
			validProductName: false
		};
	},
	mounted() {
		this.rules = tastingHelpers.getValidationRules(this.$data);
		templateHelpers.loadCustomFields(this.tasting, this.customFields);
		this.validProductName = productHelpers.checkGeneratedName(this.$set, this.selectedTastingTemplate, this.tasting, this.tastingName, 'food/beverage voucher');
	},
	computed: {

		tastingName() {
			return productHelpers.getGeneratedName(this.selectedTastingTemplate, this.tasting);
		},
		isVoucherTasting() {
			return this.selectedTastingTemplate?.code === 'bw_dollar_voucher';
		},
		isFreeTasting() {
			return this.selectedTastingTemplate?.code === 'bw_free_food_beverage';
		},
		isSpecialItem() {
			return this.selectedTastingTemplate?.code === 'bw_special_item_food_beverage';
		},
		isTastingFlight() {
			return this.selectedTastingTemplate?.code === 'bw_tasting_flight' || this.selectedTastingTemplate?.code === 'bw_tasting_flight_non_remittance';
		},
		pricePrefix() {
			return (this.partner?.currencyPrefix || '$') + ' ';
		},

	},
	methods: {
		acceptPreview() {
			productHelpers.acceptPreview(this.tasting, 'Tasting', this.preview, this.getReturnUrl());
		},
		cancelChanges() {
			window.location = this.getReturnUrl();
		},
		getReturnUrl() {
			return productHelpers.getReturnUrl(this.returnUrl, this.partner, this.venue);
		},
		rejectPreview() {
			this.isSaving = false;
			this.preview.url = undefined;
		},
		saveChanges() {
			this.tasting.name = this.tastingName;
			this.tasting.customFields = Object.values(this.customFields);

			productHelpers.saveChanges(this.isSaving, this.partner, this.venue, this.tasting, this.preview);

		}
	},
	watch: {
		selectedTastingTemplate: {
			handler(newValue) {
				this.tasting.template_id = newValue?.id || this.product.template_id;
			}
		}
	}
};
</script>